import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import {
  CheckCircle,
  ExpandMore,
  Home,
  Brush,
  Percent,
  Payment,
  Security,
  Support,
  TrendingUp
} from "@mui/icons-material";

const Pricing = () => {
  const theme = useTheme();

  const sections = [
    { id: "host-pricing", title: "For Hosts" },
    { id: "cleaner-pricing", title: "For Cleaners" },
    { id: "faq", title: "Frequently Asked Questions" },
    { id: "cta", title: "Get Started" }
  ];

  const hostPlans = [
    {
      title: "Pay‑as‑you‑go",
      price: "10%",
      period: "service fee per booking",
      description: "No subscription, pay only when you book a cleaning.",
      features: [
        "Access to vetted, professional cleaners",
        "Real‑time booking & messaging",
        "Cleaning quality guarantee",
        "Secure payment processing",
        "Free cancellation within policy window",
        "24/7 customer support"
      ],
      recommended: true
    }
  ];

  const cleanerFeeStructure = [
    { label: "Commission per job", value: "0%", description: "Deducted from the cleaning price you earn" },
    { label: "Payment processing fee", value: "2.9% + $0.30", description: "Standard Stripe fee for payouts" },
    { label: "Certification fee", value: "Free", description: "No cost to become FreshSweeper certified" },
    { label: "Monthly subscription", value: "$0", description: "No monthly fees – pay only when you work" }
  ];

  const faqs = [
    {
      question: "Do hosts pay a subscription fee?",
      answer: "No. Hosts only pay a 10% service fee on each completed booking. There are no monthly or hidden fees."
    },
    {
      question: "Who sets the cleaning price?",
      answer: "The host sets the price for each cleaning job. You choose the rate you’re comfortable paying, and cleaners see that offer before accepting."
    },
    {
      question: "How does the cleaner get paid?",
      answer: "The cleaner receives the full price set by the host, minus a 0% platform commission and standard payment processing fees (2.9% + $0.30). The host’s 10% service fee is added on top at checkout."
    },
    {
      question: "Is there a minimum or maximum price?",
      answer: "Hosts can set any reasonable price. We provide suggested rates based on your property size and local market to help you stay competitive."
    },
    {
      question: "Are there any hidden costs?",
      answer: "No. Both sides see all fees upfront before confirming a booking. There are no surprises."
    },
    {
      question: "How do cancellations affect fees?",
      answer: "Cancellation policies are shown during booking. Depending on timing, hosts may be charged a cancellation fee (e.g., 25–100% of the cleaning fee). Cleaners who cancel without valid reason may face penalties."
    },
    {
      question: "Can a cleaner negotiate the price?",
      answer: "The host sets the initial price. Cleaners can choose to accept or decline. If a cleaner believes the price is too low, they can send a counter‑offer or simply pass on the job."
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Transparent Pricing"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Simple, Transparent Pricing
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          No hidden fees. No subscriptions. Pay only when you use FreshSweeper – whether you’re a host or a cleaner.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 100,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              On this page
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, listStyle: "none" }}>
              {sections.map((section) => (
                <Box component="li" key={section.id} sx={{ mb: 0.5 }}>
                  <Link
                    href={`#${section.id}`}
                    underline="hover"
                    sx={{
                      fontSize: "0.85rem",
                      color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.primary.main },
                      textDecoration: "none"
                    }}
                  >
                    {section.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* Host Pricing */}
            <Box id="host-pricing" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                For Hosts – Pay Only When You Book
              </Typography>
              <Typography variant="body1" paragraph>
                Hosts set the cleaning price and pay a simple service fee on each completed cleaning. No subscription, no setup fees. You see the total price including all fees before confirming a booking.
              </Typography>
              <Grid container spacing={3}>
                {hostPlans.map((plan, idx) => (
                  <Grid item xs={12} md={12} key={idx}>
                    <Card
                      sx={{
                        borderRadius: 4,
                        border: plan.recommended ? `2px solid ${theme.palette.primary.main}` : undefined,
                        boxShadow: plan.recommended ? 4 : 1
                      }}
                    >
                      {plan.recommended && (
                        <Box
                          sx={{
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            textAlign: 'center',
                            py: 0.5,
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16
                          }}
                        >
                          <Typography variant="caption" fontWeight={600}>Most popular</Typography>
                        </Box>
                      )}
                      <CardHeader
                        title={plan.title}
                        subheader={
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
                            <Typography variant="h3" fontWeight={700}>{plan.price}</Typography>
                            <Typography variant="body2" color="text.secondary">{plan.period}</Typography>
                          </Box>
                        }
                      />
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {plan.description}
                        </Typography>
                        <List dense>
                          {plan.features.map((feature, i) => (
                            <ListItem key={i} disableGutters>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircle color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={feature} />
                            </ListItem>
                          ))}
                        </List>
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{ mt: 2 }}
                          href="/"
                        >
                          Get started as a Host
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Cleaner Pricing */}
            <Box id="cleaner-pricing" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                For Cleaners – Keep More of What You Earn
              </Typography>
              <Typography variant="body1" paragraph>
                Hosts set the cleaning price. You choose which jobs to accept. Freshsweeper deducts a small commission only when you complete a job. No monthly fees, no surprises.
              </Typography>
              <Grid container spacing={2}>
                {cleanerFeeStructure.map((fee, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        height: '100%'
                      }}
                    >
                      <Typography variant="h6" fontWeight={700}>{fee.value}</Typography>
                      <Typography variant="subtitle2">{fee.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{fee.description}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}>
                <Typography variant="body2">
                  💡 <strong>Example:</strong> A host offers $100 for a cleaning. 
                  You accept. FreshSweeper deducts 10% ($0) + payment processing fee (2.9% + $0.30 ≈ $3.20). 
                  You receive <strong>$96.80</strong> – the host pays an additional 15% service fee on top.
                </Typography>
              </Box>
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                href="/cleaner"
              >
                Become a Cleaner
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* FAQ */}
            <Box id="faq" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Frequently Asked Questions
              </Typography>
              {faqs.map((faq, idx) => (
                <Accordion key={idx} elevation={0} sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* CTA */}
            <Box id="cta" sx={{ scrollMarginTop: 80, mb: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                  borderRadius: 4
                }}
              >
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Ready to get started?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
                  Join thousands of hosts and cleaners who trust Freshsweeper for reliable, affordable cleaning services.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button variant="contained" size="large" href="/">
                    I’m a Host
                  </Button>
                  <Button variant="outlined" size="large" href="/leaner">
                    I’m a Cleaner
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Pricing;